import { CHAT_TYPES } from "@/js/constants/chat";
import {
  coerceMonarchOptionalString,
  unwrapMonarchObjectScalars,
} from "@/js/helpers/unwrapMonarchScalar";

const VALID_CHAT_TYPES = new Set(Object.values(CHAT_TYPES));

const TRIGGER_TYPE_ALIASES = {
  Idle: "idle",
  AddressEntry: "addressEntry",
  "Address Entry": "addressEntry",
  FAQs: "faqs",
  ReturnedToTop: "returnedToTop",
  "Scroll Up": "returnedToTop",
  ScrollUp: "returnedToTop",
  ProductCardIdle: "productCardIdle",
  "Product Card Idle": "productCardIdle",
};

const TRIGGER_TYPE_COMPACT = {
  idle: "idle",
  addressentry: "addressEntry",
  faqs: "faqs",
  returnedtotop: "returnedToTop",
  scrollup: "returnedToTop",
  productcardidle: "productCardIdle",
};

export const resolveMonarchTriggerTypeKey = (type) => {
  if (type == null || type === "") return null;
  if (typeof type === "string" && TRIGGER_TYPE_ALIASES[type]) {
    return TRIGGER_TYPE_ALIASES[type];
  }
  const compact = String(type).toLowerCase().replace(/\s+/g, "");
  return TRIGGER_TYPE_COMPACT[compact] || null;
};

const MLP_TRIGGER_KEYS = [
  "idle",
  "addressEntry",
  "faqs",
  "returnedToTop",
  "productCardIdle",
];

/**
 * Monarch `triggers` shapes (newest first):
 * - Rules list: `{ triggerRules: [{ type, timeout, popModal? }], popModal? }` — top-level `popModal` applies to rows that omit it.
 * - Flat row: `{ type: "Idle", timeout, popModal }`
 * - Array rows: `[{ type, timeout, popModal? }]`
 * - Keyed: `{ idle: { timeout, popModal }, ... }`
 *
 * Output is always keyed for hooks: `triggers.idle`, `triggers.addressEntry`, `triggers.faqs`, `triggers.returnedToTop`.
 */
export const normalizeMonarchChatConfig = (raw) => {
  if (!raw || typeof raw !== "object") {
    return { chatType: CHAT_TYPES.NONE, triggers: {} };
  }

  const {
    triggers: rawTriggers,
    chatType: rawChatType,
    sdkVersion: rawSdkVersion,
    triggerType: rawTriggerType,
    pageType: rawPageType,
    proactive: rawProactive,
    ...rest
  } = raw;

  const sdkVersion = coerceMonarchOptionalString(rawSdkVersion);
  const triggerType = coerceMonarchOptionalString(rawTriggerType);
  const pageType = coerceMonarchOptionalString(rawPageType);

  let proactive = rawProactive;
  if (proactive && typeof proactive === "object") {
    proactive = unwrapMonarchObjectScalars(proactive, [
      "message",
      "type",
      "display",
    ]);
  }

  let chatType = coerceMonarchOptionalString(rawChatType) ?? rawChatType;

  let triggers = {};

  if (Array.isArray(rawTriggers)) {
    rawTriggers.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const rowType = coerceMonarchOptionalString(row.type) ?? row.type;
      const key = resolveMonarchTriggerTypeKey(rowType);
      if (!key) {
        return;
      }
      const entry = { timeout: row.timeout };
      if (row.popModal !== undefined) entry.popModal = row.popModal;
      if (row.proactiveModalContent !== undefined)
        entry.proactiveModalContent = row.proactiveModalContent;
      if (row.proactive !== undefined) {
        let rowProactive = row.proactive;
        if (rowProactive && typeof rowProactive === "object") {
          rowProactive = unwrapMonarchObjectScalars(rowProactive, [
            "message",
            "type",
            "display",
          ]);
        }
        entry.proactive = rowProactive;
      }
      triggers[key] = entry;
    });
  } else if (rawTriggers && typeof rawTriggers === "object") {
    const triggerRules = rawTriggers.triggerRules;
    const hasTriggerRules =
      Array.isArray(triggerRules) && triggerRules.length > 0;

    if (hasTriggerRules) {
      const hasParentPopModalKey = Object.prototype.hasOwnProperty.call(
        rawTriggers,
        "popModal",
      );
      const parentPopModal = hasParentPopModalKey
        ? Boolean(rawTriggers.popModal)
        : false;

      triggerRules.forEach((row) => {
        if (!row || typeof row !== "object") return;
        const rowType = coerceMonarchOptionalString(row.type) ?? row.type;
        const key = resolveMonarchTriggerTypeKey(rowType);
        if (!key) return;
        const hasRowPopModal = Object.prototype.hasOwnProperty.call(
          row,
          "popModal",
        );
        const popModal = hasRowPopModal
          ? Boolean(row.popModal)
          : parentPopModal;
        const entry = {
          ...(row.timeout !== undefined && { timeout: row.timeout }),
          popModal,
        };
        if (row.proactiveModalContent !== undefined)
          entry.proactiveModalContent = row.proactiveModalContent;
        if (row.proactive !== undefined) {
          let rowProactive = row.proactive;
          if (rowProactive && typeof rowProactive === "object") {
            rowProactive = unwrapMonarchObjectScalars(rowProactive, [
              "message",
              "type",
              "display",
            ]);
          }
          entry.proactive = rowProactive;
        }
        triggers[key] = entry;
      });
    } else {
      const hasKeyedMlp = MLP_TRIGGER_KEYS.some(
        (k) => rawTriggers[k] !== undefined,
      );

      const flatType =
        coerceMonarchOptionalString(rawTriggers.type) ?? rawTriggers.type;
      const flatKey = resolveMonarchTriggerTypeKey(flatType);
      const looksLikeFlatRow =
        !hasKeyedMlp &&
        flatKey &&
        (rawTriggers.timeout !== undefined ||
          rawTriggers.popModal !== undefined);

      if (looksLikeFlatRow) {
        const {
          type: _t,
          timeout,
          popModal,
          triggerRules: _rules,
          ...restTriggers
        } = rawTriggers;
        triggers = { ...restTriggers };
        const hasPopModalKey = Object.prototype.hasOwnProperty.call(
          rawTriggers,
          "popModal",
        );
        triggers[flatKey] = {
          ...(timeout !== undefined && { timeout }),
          popModal: hasPopModalKey ? Boolean(popModal) : false,
        };
      } else {
        const { triggerRules: _omitRules, ...restTriggers } = rawTriggers;
        triggers = { ...restTriggers };
      }
    }
  }

  let anyPopModalTrue = false;
  if (Array.isArray(rawTriggers)) {
    anyPopModalTrue = rawTriggers.some((row) => row?.popModal === true);
  } else if (rawTriggers && typeof rawTriggers === "object") {
    const rules = rawTriggers.triggerRules;
    if (Array.isArray(rules) && rules.length > 0) {
      if (rawTriggers.popModal === true) {
        anyPopModalTrue = true;
      } else {
        anyPopModalTrue = rules.some((row) => row?.popModal === true);
      }
    } else if (rawTriggers.popModal === true) {
      anyPopModalTrue = true;
    } else {
      anyPopModalTrue = Object.entries(rawTriggers).some(
        ([key, row]) =>
          key !== "triggerRules" &&
          row &&
          typeof row === "object" &&
          !Array.isArray(row) &&
          row.popModal === true,
      );
    }
  }

  if (!chatType || !VALID_CHAT_TYPES.has(chatType)) {
    chatType = anyPopModalTrue ? CHAT_TYPES.PROACTIVE_V2 : CHAT_TYPES.GIGA;
  }

  return {
    ...rest,
    ...(sdkVersion != null && { sdkVersion }),
    ...(triggerType != null && { triggerType }),
    ...(pageType != null && { pageType }),
    ...(proactive !== undefined && { proactive }),
    chatType,
    triggers,
  };
};
